/* eslint-disable @typescript-eslint/no-explicit-any */
import DataTable from "@/components/data-table";
import { DatePickerWithRange } from "@/components/date-range";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  useEmailHealthExport,
  useEmailHealthHistory,
} from "@/hooks/use-emailHealth";
import { usePublishReport, useUnPublishReport } from "@/hooks/use-postmaster";
import PublishReportButton from "@/pages/email-health/publish-button";
import { EmailHealthResult } from "@/types/email-health";
import { useQueryClient } from "@tanstack/react-query";
import { ColDef } from "ag-grid-community";
import { addDays } from "date-fns";
import {
  CheckCircle2,
  ChevronDown,
  Clock,
  Download,
  FileDown,
  FileUp,
  Globe,
  Mail,
  MinusCircle,
  Server,
  XCircle,
} from "lucide-react";
import { useCallback, useState } from "react";
import { DateRange } from "react-day-picker";
import { toast } from "sonner";
import { PublishLinksDialog } from "./publish-links-dialog";

interface BlacklistRecord {
  category: string;
  host: string;
  result: string;
}

interface MXServerInfo {
  server: string;
  ip: string;
  priority: number;
  blacklists: BlacklistRecord[];
}

export function EmailHealthHistory({ domain }: { domain: string }) {
  // Helper function to get the first available WHOIS record key
  const getWhoisKey = (whoisData: any) => {
    if (!whoisData) return null;
    // Get the first key from the WHOIS data object
    return Object.keys(whoisData)[0] || null;
  };

  // Helper function to get WHOIS data regardless of registrar key
  const getWhoisData = (record: any) => {
    if (!record?.record?.record) return null;
    const whoisKey = getWhoisKey(record.record.record);
    return whoisKey ? record.record.record[whoisKey] : null;
  };

  const [selectedRows, setSelectedRows] = useState<EmailHealthResult[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: addDays(new Date(), -7), // Default to last 7 days
    to: new Date(),
  });
  // console.log(whoisrecord, "whoisrecord");
  const { data: healthHistory, isLoading: isHealthHistoryLoading } =
    useEmailHealthHistory(
      currentPage,
      limit,
      dateRange.from || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // Default to 7 days ago
      dateRange.to || new Date(), // Default to now
      domain
    );

  const [showPublishLinksDialog, setShowPublishLinksDialog] = useState(false);

  const { exportResults, isExporting } = useEmailHealthExport();
  const queryClient = useQueryClient();

  const {
    mutateAsync: publishReport,
    data: publishedLinksData,
    isPending: isPublishing,
  } = usePublishReport();

  // console.log(publishedLinksData, "publishedLinksData");

  const { mutateAsync: unPublishReport, isPending: isUnPublishing } =
    useUnPublishReport();

  const handleExport = () => {
    exportResults({
      from: dateRange.from || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      to: dateRange.to || new Date(),
      domain,
    });
  };

  const handlePublish = async (flag: "publish" | "unpublish") => {
    try {
      const reportIds = selectedRows.map((row) => row._id);

      if (flag === "publish") {
        await publishReport({
          domain,
          reportIds,
        });
        setShowPublishLinksDialog(true);
        toast.success("Report published successfully");
        window.cioanalytics?.track("email_health_test_bulk_published", {
          count: reportIds.length,
          domain: domain,
        });
      } else {
        await unPublishReport({
          domain,
          reportIds,
        });
        toast.success("Report unpublished successfully");
        window.cioanalytics?.track("email_health_test_bulk_unpublished", {
          count: reportIds.length,
          domain: domain,
        });
      }

      setSelectedRows([]);

      queryClient.invalidateQueries({
        queryKey: [
          "emailHealthHistory",
          currentPage,
          limit,
          dateRange.from || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          dateRange.to || new Date(),
          domain,
        ],
      });
    } catch (error) {
      toast.error("Failed to publish report");
    }
  };

  const onSelectionChanged = useCallback((event: any) => {
    const selected = event.api.getSelectedRows() || [];
    setSelectedRows(selected);
    console.log(selected);
  }, []);

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pass: "bg-green-200 text-green-800",
      fail: "bg-red-200 text-red-800",
      pending: "bg-yellow-200 text-yellow-800",
      partial: "bg-orange-200 text-orange-800",
    };

    return (
      <Badge className={colors[status?.toLowerCase()] || "bg-gray-200"}>
        {status || "N/A"}
      </Badge>
    );
  };

  const StatusIconRenderer = ({ status }: { status: string | undefined }) => {
    return status?.toLowerCase() === "pass" ? (
      <div className="flex h-full items-center  w-full">
        <CheckCircle2 className="h-5 w-5 text-green-500" />
      </div>
    ) : (
      <div className="flex h-full items-center  w-full">
        <XCircle className="h-5 w-5 text-red-500" />
      </div>
    );
  };

  const OverallStatusRenderer = ({ status }: { status: any }) => {
    const getStatusConfig = (score: number | null) => {
      if (score === null || score === undefined) {
        return {
          icon: <MinusCircle className="h-4 w-4 text-slate-400" />,
          text: "N/A",
          bgColor: "bg-slate-50",
          textColor: "text-slate-600",
          borderColor: "border-slate-200",
          description: "No data available",
        };
      }

      if (score > 90) {
        return {
          icon: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
          text: "Excellent",
          bgColor: "bg-emerald-50",
          textColor: "text-emerald-700",
          borderColor: "border-emerald-200",
          description: "Score > 90",
        };
      } else if (score >= 80) {
        return {
          icon: <CheckCircle2 className="h-4 w-4 text-blue-500" />,
          text: "Good",
          bgColor: "bg-blue-50",
          textColor: "text-blue-700",
          borderColor: "border-blue-200",
          description: "Score 80-90",
        };
      } else if (score >= 70) {
        return {
          icon: <Clock className="h-4 w-4 text-amber-500" />,
          text: "Average",
          bgColor: "bg-amber-50",
          textColor: "text-amber-700",
          borderColor: "border-amber-200",
          description: "Score 70-80",
        };
      } else {
        return {
          icon: <XCircle className="h-4 w-4 text-red-500" />,
          text: "Poor",
          bgColor: "bg-red-50",
          textColor: "text-red-700",
          borderColor: "border-red-200",
          description: "Score < 70",
        };
      }
    };

    const score = status ? Number(status) : null;
    const config = getStatusConfig(score);

    return (
      <div className="flex w-full h-full items-center">
        {/* <div className="flex justify-between px-3 text-xs text-slate-500">
          <span>Status</span>
          <span>Score</span>
        </div> */}
        <div className="flex items-center justify-between px-3 mt-0.5">
          <div className="flex items-center gap-1.5">
            {config.icon}
            <span className={`text-sm font-medium ${config.textColor}`}>
              {config.text}
            </span>
          </div>
          {/* <span className={`text-sm font-medium ${config.textColor}`}>
            {status || 0}%
          </span> */}
        </div>
      </div>
    );
  };

  const ScoreRenderer = ({ value }: { value: number }) => {
    const getScoreConfig = (score: number) => {
      if (score >= 80) {
        return {
          color: "bg-green-500",
          textColor: "text-green-700 dark:text-green-300",
          bgColor: "bg-green-100 dark:bg-white/30",
        };
      } else if (score >= 50) {
        return {
          color: "bg-yellow-500",
          textColor: "text-yellow-700 dark:text-yellow-300",
          bgColor: "bg-yellow-100 dark:bg-white/30",
        };
      } else {
        return {
          color: "bg-red-500",
          textColor: "text-red-700 dark:text-red-300",
          bgColor: "bg-red-100 dark:bg-white/30",
        };
      }
    };

    const config = getScoreConfig(value);

    return (
      <div className="flex items-center h-full w-full gap-2">
        <div className={`flex-1 h-2 rounded-full ${config.bgColor}`}>
          <div
            className={`h-full rounded-full ${config.color}`}
            style={{ width: `${value}%` }}
          />
        </div>
        <span className={`text-sm font-medium ${config.textColor}`}>
          {value}%
        </span>
      </div>
    );
  };

  const BlacklistDialog = ({ data }: { data: any }) => {
    const renderBlacklistSection = (
      title: string,
      icon: React.ReactNode,
      description: string,
      items: any[],
      type: "ip" | "domain"
    ) => {
      if (!items?.length) return null;

      return (
        <Collapsible defaultOpen>
          <Card>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="pb-3 hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    {icon}
                    {title}
                    <Badge variant="outline" className="ml-2">
                      {items.length}
                    </Badge>
                  </CardTitle>
                  <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform duration-200" />
                </div>
                <CardDescription className="self-start">
                  {description}
                </CardDescription>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="p-0">
                <div className="space-y-2">
                  {items.map((serverData: any, index: number) => {
                    const blacklists =
                      type === "domain"
                        ? serverData.blacklists
                        : [
                            ...(serverData.blacklists || []),
                            ...(serverData.domainBlacklistsResults || []),
                          ];

                    const listedCount = blacklists.filter(
                      (bl: any) => bl.listed
                    ).length;

                    return (
                      <Collapsible key={serverData.server}>
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center justify-between p-4 hover:bg-muted/50">
                            <div className="flex items-center gap-2">
                              {listedCount > 0 ? (
                                <XCircle className="w-4 h-4 text-red-500" />
                              ) : (
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                              )}
                              <div className="flex flex-col">
                                <span className="text-sm font-medium">
                                  {serverData.server}
                                </span>
                                <span className="text-xs self-start text-muted-foreground">
                                  {type === "ip" ? "IP Address" : "Domain"}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={
                                  listedCount === 0 ? "success" : "destructive"
                                }
                              >
                                {listedCount === 0
                                  ? "Clean"
                                  : `${listedCount} Listed`}
                              </Badge>
                              <Badge variant="outline">
                                {blacklists.length} checks
                              </Badge>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="border-t">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b bg-muted/50">
                                  <th className="py-2 px-4 text-left font-medium">
                                    Blacklist
                                  </th>
                                  <th className="py-2 px-4 text-left font-medium">
                                    Status
                                  </th>
                                  <th className="py-2 px-4 text-left font-medium">
                                    Response
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {blacklists.map(
                                  (blacklist: any, idx: number) => (
                                    <tr
                                      key={idx}
                                      className={
                                        blacklist.listed ? "bg-red-50/50" : ""
                                      }
                                    >
                                      <td className="py-2 px-4">
                                        <div className="flex items-center gap-2">
                                          {blacklist.listed ? (
                                            <XCircle className="w-4 h-4 text-red-500" />
                                          ) : (
                                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                                          )}
                                          <span className="text-gray-700 font-medium">
                                            {blacklist.rblName}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="py-2 px-4">
                                        <Badge
                                          variant={
                                            blacklist.listed
                                              ? "destructive"
                                              : "success"
                                          }
                                        >
                                          {blacklist.listed
                                            ? "Listed"
                                            : "Clean"}
                                        </Badge>
                                      </td>
                                      <td className="py-2 px-4 text-gray-500">
                                        {blacklist.response || "No response"}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      );
    };

    return (
      <div className="space-y-4">
        {renderBlacklistSection(
          "MX Server Blacklists",
          <Server className="w-4 h-4 text-blue-500" />,
          "Mail server IP reputation checks",
          data.mxServerBlacklists || [],
          "ip"
        )}

        {renderBlacklistSection(
          "Domain Blacklists",
          <Globe className="w-4 h-4 text-purple-500" />,
          "Domain reputation and URL blacklists",
          data.domainBlacklist || [],
          "domain"
        )}

        {renderBlacklistSection(
          "SMTP Server Blacklists",
          <Mail className="w-4 h-4 text-orange-500" />,
          "Email sending behavior and authentication",
          data.smtpBlacklist || [],
          "ip"
        )}
      </div>
    );
  };

  const colDefs: ColDef[] = [
    {
      field: "from.email",
      headerName: "Email",
      minWidth: 250,
      width: 250,
      cellRenderer: (params: any) => (
        <span className="text-sm font-medium">{params.data.from.email}</span>
      ),
    },
    {
      colId: "overallStatus",
      headerName: "Overall Status",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <OverallStatusRenderer status={params.data.results.score} />
      ),
    },
    {
      colId: "score",
      headerName: "Score",
      minWidth: 150,
      valueGetter: (params: any) => params.data.results.score || 0,
      cellRenderer: (params: any) => (
        <ScoreRenderer value={params.data.results.score || 0} />
      ),
    },
    {
      field: "createdAt",
      headerName: "Checked At",
      width: 180,
      minWidth: 200,
      cellRenderer: (params: any) =>
        new Date(params.value).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },

    {
      headerName: "DKIM",
      minWidth: 150,
      cellRenderer: (params: any) => {
        return (
          <StatusIconRenderer
            status={
              params.data.results.records.find(
                (record: any) => record.type === "DKIM"
              )?.result
                ? "pass"
                : "fail"
            }
          />
        );
      },
    },
    {
      headerName: "SPF",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <StatusIconRenderer
          status={
            params.data.results.records.find(
              (record: any) => record.type === "SPF"
            )?.result
              ? "pass"
              : "fail"
          }
        />
      ),
    },
    {
      headerName: "DMARC",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <StatusIconRenderer
          status={
            params.data.results.records.find(
              (record: any) => record.type === "DMARC"
            )?.result
              ? "pass"
              : "fail"
          }
        />
      ),
    },
    {
      headerName: "ARC",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <StatusIconRenderer
          status={
            params.data.results.records.find(
              (record: any) => record.type === "ARC"
            )?.result
              ? "pass"
              : "fail"
          }
        />
      ),
    },
    {
      headerName: "BIMI",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <StatusIconRenderer
          status={
            params.data.results.records.find(
              (record: any) => record.type === "BIMI"
            )?.result
              ? "pass"
              : "fail"
          }
        />
      ),
    },
    {
      headerName: "A Record",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div className="flex h-full items-center w-full">
          <StatusIconRenderer
            status={
              params.data.results.records.find(
                (record: any) => record.type === "A"
              )?.result
                ? "pass"
                : "fail"
            }
          />
          {/* <span className="text-sm text-gray-600">
            {params.value?.found ? 'Record found' : 'No record found'}
          </span> */}
        </div>
      ),
    },
    {
      headerName: "MX Record",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div className="flex h-full items-center w-full">
          <StatusIconRenderer
            status={
              params.data.results.records.find(
                (record: any) => record.type === "MX"
              )?.result
                ? "pass"
                : "fail"
            }
          />
          {/* <span className="text-sm text-gray-600">
            {params.value?.found ? 'Record found' : 'No record found'}
          </span> */}
        </div>
      ),
    },

    {
      headerName: "DNSKEY",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div className="flex h-full items-center w-full">
          <StatusIconRenderer
            status={
              params.data.results.records.find(
                (record: any) => record.type === "DNSKEY"
              )?.result
                ? "pass"
                : "fail"
            }
          />
          {/* <span className="text-sm text-gray-600">
            {params.value?.found ? 'Record found' : 'No record found'}
          </span> */}
        </div>
      ),
    },
    {
      headerName: "DNSSEC",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div className="flex h-full items-center w-full">
          <StatusIconRenderer
            status={
              params.data.results.records.find(
                (record: any) => record.type === "DNSSEC"
              )?.result
                ? "pass"
                : "fail"
            }
          />
          {/* <span className="text-sm text-gray-600">
            {params.value?.found ? 'Domain Name System Security Extensions' : 'No record found'}
          </span> */}
        </div>
      ),
    },
    {
      headerName: "RPKI",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div className="flex h-full items-center w-full">
          <StatusIconRenderer
            status={
              params.data.results.records.find(
                (record: any) => record.type === "RPKI"
              )?.result
                ? "pass"
                : "fail"
            }
          />
          {/* <span className="text-sm text-gray-600">
            {params.value?.found ? 'Route Origin Authorization status' : 'No record found'}
          </span> */}
        </div>
      ),
    },
    {
      headerName: "PTR",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div className="flex h-full items-center w-full">
          <StatusIconRenderer
            status={
              params.data.results.records.find(
                (record: any) => record.type === "PTR"
              )?.result
                ? "pass"
                : "fail"
            }
          />
          {/* <span className="text-sm text-gray-600">
            {params.value?.found ? 'Route Origin Authorization status' : 'No record found'}
          </span> */}
        </div>
      ),
    },

    {
      headerName: "DANE",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div className="flex h-full items-center w-full">
          <StatusIconRenderer
            status={
              params.data.results.records.find(
                (record: any) => record.type === "DANE"
              )?.result
                ? "pass"
                : "fail"
            }
          />
          {/* <span className="text-sm text-gray-600">
            {params.value?.found ? 'DNS-based Authentication of Named Entities for TLS' : 'No record found'}
          </span> */}
        </div>
      ),
    },
    {
      headerName: "TLS",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div className="flex h-full items-center w-full">
          <StatusIconRenderer
            status={
              params.data.results.records.find(
                (record: any) => record.type === "TLS"
              )?.result
                ? "pass"
                : "fail"
            }
          />
          {/* <span className="text-sm text-gray-600">
            {params.value?.found ? 'Transport Layer Security Configuration' : 'No record found'}
          </span> */}
        </div>
      ),
    },
    {
      headerName: "STARTTLS",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div className="flex h-full items-center w-full">
          <StatusIconRenderer
            status={
              params.data.results.records.find(
                (record: any) => record.type === "STARTTLS"
              )?.result
                ? "pass"
                : "fail"
            }
          />
          {/* <span className="text-sm text-gray-600">
            {params.value?.found ? 'Email Transport Encryption' : 'No record found'}
          </span> */}
        </div>
      ),
    },
    {
      headerName: "MTA-STS",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div className="flex h-full items-center w-full">
          <StatusIconRenderer
            status={
              params.data.results.records.find(
                (record: any) => record.type === "MTA-STS"
              )?.result
                ? "pass"
                : "fail"
            }
          />
          {/* <span className="text-sm text-gray-600">
            {params.value?.found ? 'SMTP MTA Strict Transport Security' : 'No record found'}
          </span> */}
        </div>
      ),
    },
    {
      field: "blacklist",
      headerName: "Blacklist",
      filter: true,
      sortable: true,
      minWidth: 250,
      flex: 1,
      cellRenderer: (params: any) => {
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="w-full">
                Check Blacklists
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[60vw] flex flex-col  h-[80vh] overflow-y-auto">
              <DialogTitle>Blacklist Check Results</DialogTitle>
              <BlacklistDialog data={params.data} />
            </DialogContent>
          </Dialog>
        );
      },
    },
    {
      field: "WHOIS",
      headerName: "whois",
      filter: true,
      sortable: true,
      minWidth: 250,
      flex: 1,
      cellRenderer: (params: any) => {
        // console.log(whoisrecord, "whoisrecord");
        const whoisrecord = params.data.results.records.find(
          (record: any) => record.type === "WHOIS"
        );

        // console.log(whoisrecord, "whoisrecord");
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="w-full">
                Check WHOIS
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[60vw] flex flex-col  h-[80vh] overflow-y-auto">
              <DialogTitle>WHOIS Check Results</DialogTitle>
              <div className="space-y-4">
                <p className="text-slate-600">
                  {whoisrecord.result
                    ? "WHOIS record found and verified"
                    : "WHOIS verification failed"}
                </p>

                <div className="bg-slate-50 rounded-lg p-4">
                  {/* Domain Information */}
                  <div className="mb-5">
                    <h3 className="text-sm font-semibold mb-3 text-slate-700 border-b pb-1">
                      Domain Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-slate-600">
                          Domain Name:
                        </span>
                        <span className="text-slate-800">
                          {
                            whoisrecord.record?.record?.["whois.nic.co"]?.[
                              "Domain Name"
                            ]
                          }
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-slate-600">
                          Registry ID:
                        </span>
                        <span className="text-slate-800">
                          {
                            whoisrecord.record?.record?.["whois.nic.co"]?.[
                              "Registry Domain ID"
                            ]
                          }
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-slate-600">
                          Created:
                        </span>
                        <span className="text-slate-800">
                          {
                            whoisrecord.record?.record?.["whois.nic.co"]?.[
                              "Created Date"
                            ]
                          }
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-slate-600">
                          Updated:
                        </span>
                        <span className="text-slate-800">
                          {
                            whoisrecord.record?.record?.["whois.nic.co"]?.[
                              "Updated Date"
                            ]
                          }
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-slate-600">
                          Expires:
                        </span>
                        <span className="text-slate-800">
                          {
                            whoisrecord.record?.record?.["whois.nic.co"]?.[
                              "Expiry Date"
                            ]
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Registrar Information */}
                  <div className="mb-5">
                    <h3 className="text-sm font-semibold mb-3 text-slate-700 border-b pb-1">
                      Registrar Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-slate-600">
                          Registrar:
                        </span>
                        <span className="text-slate-800">
                          {
                            whoisrecord.record?.record?.["whois.nic.co"]?.[
                              "Registrar"
                            ]
                          }
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-slate-600">
                          IANA ID:
                        </span>
                        <span className="text-slate-800">
                          {
                            whoisrecord.record?.record?.["whois.nic.co"]?.[
                              "Registrar IANA ID"
                            ]
                          }
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-slate-600">URL:</span>
                        <span className="text-slate-800">
                          {
                            whoisrecord.record?.record?.["whois.nic.co"]?.[
                              "Registrar URL"
                            ]
                          }
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-slate-600">
                          Abuse Email:
                        </span>
                        <span className="text-slate-800">
                          {
                            whoisrecord.record?.record?.["whois.nic.co"]?.[
                              "Registrar Abuse Contact Email"
                            ]
                          }
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-slate-600">
                          Abuse Phone:
                        </span>
                        <span className="text-slate-800">
                          {
                            whoisrecord.record?.record?.["whois.nic.co"]?.[
                              "Registrar Abuse Contact Phone"
                            ]
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Domain Status */}
                  <div className="mb-5">
                    <h3 className="text-sm font-semibold mb-3 text-slate-700 border-b pb-1">
                      Domain Status
                    </h3>
                    <div className="space-y-2">
                      {whoisrecord.record?.record?.["whois.nic.co"]?.[
                        "Domain Status"
                      ]?.map((status: string, idx: number) => {
                        const statusParts = status.split(" ");
                        const statusType = statusParts[0];
                        const statusUrl = statusParts[1] || "";
                        return (
                          <div key={idx} className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                            <span className="text-sm font-medium">
                              {statusType}
                            </span>
                            {statusUrl && (
                              <span className="text-xs text-slate-500">
                                {statusUrl}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Name Servers */}
                  <div className="mb-5">
                    <h3 className="text-sm font-semibold mb-3 text-slate-700 border-b pb-1">
                      Name Servers
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {whoisrecord.record?.record?.["whois.nic.co"]?.[
                        "Name Server"
                      ]?.map((ns: string, idx: number) => (
                        <div
                          key={idx}
                          className="bg-white p-2 rounded border border-slate-200 text-sm"
                        >
                          {ns}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* DNSSEC Information */}
                  {whoisrecord.record?.record?.["whois.nic.co"]?.["DNSSEC"] && (
                    <div className="mb-5">
                      <h3 className="text-sm font-semibold mb-3 text-slate-700 border-b pb-1">
                        DNSSEC
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-2">
                          <span className="font-medium text-slate-600">
                            Status:
                          </span>
                          <span className="text-slate-800">
                            {
                              whoisrecord.record?.record?.["whois.nic.co"]?.[
                                "DNSSEC"
                              ]
                            }
                          </span>
                        </div>
                        {whoisrecord.record?.record?.["whois.nic.co"]?.[
                          "DNSSEC DS Data"
                        ] && (
                          <div className="flex items-start gap-2">
                            <span className="font-medium text-slate-600">
                              DS Data:
                            </span>
                            <span className="text-slate-800 font-mono text-xs break-all">
                              {
                                whoisrecord.record?.record?.["whois.nic.co"]?.[
                                  "DNSSEC DS Data"
                                ]
                              }
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Additional Information */}
                  {whoisrecord.record?.record?.["whois.nic.co"]?.text && (
                    <div>
                      <h3 className="text-sm font-semibold mb-3 text-slate-700 border-b pb-1">
                        Additional Information
                      </h3>
                      <div className="space-y-1 max-h-40 overflow-y-auto text-xs text-slate-600 bg-white p-3 rounded border border-slate-200">
                        {whoisrecord.record?.record?.["whois.nic.co"]?.text.map(
                          (line: string, idx: number) => (
                            <p key={idx}>{line}</p>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        );
      },
    },
    {
      headerName: "Published",
      minWidth: 180,
      cellRenderer: (params: any) => (
        <PublishReportButton
          domain={params.data.domain}
          companyId={params.data.companyId}
          reportId={params.data._id}
          isPublic={params.data.isPublic}
          type="history-table"
        />
      ),
    },
    {
      field: "publishedAt",
      headerName: "Published At",
      minWidth: 200,
      cellRenderer: (params: any) => {
        return params.data.isPublic
          ? new Date(params.value).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "N/A";
      },
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-col md:flex-row items-end justify-end gap-2">
        {selectedRows.length > 0 && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePublish("publish")}
              disabled={isPublishing}
            >
              <FileUp className="w-4 h-4" />
              Publish
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePublish("unpublish")}
              disabled={isUnPublishing}
            >
              <FileDown className="w-4 h-4" />
              Unpublish
            </Button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <DatePickerWithRange
            align="end"
            date={dateRange}
            onDateChange={(date) =>
              setDateRange(date || { from: new Date(), to: new Date() })
            }
          />
          <Button
            variant="outline"
            size="sm"
            className="disabled:cursor-not-allowed"
            onClick={handleExport}
            disabled={isExporting || !healthHistory?.data.length}
          >
            <Download className="w-4 h-4 mr-2" />
            {isExporting ? "Exporting..." : "Export"}
          </Button>
        </div>
      </div>

      <DataTable
        rowData={healthHistory?.data ?? []}
        columnDefs={colDefs}
        height={550}
        showPagination={true}
        onSelectionChanged={onSelectionChanged}
        defaultColDef={{
          editable: false,
          resizable: true,
          minWidth: 150,
          sortable: true,
          filter: true,
        }}
        paginationProps={{
          pageSize: limit,
          totalPages: Math.ceil((healthHistory?.total || 0) / limit),
          currentPage,
          onPageChange: setCurrentPage,
          rowsPerPage: limit,
          onRowsPerPageChange: setLimit,
        }}
        loading={isHealthHistoryLoading}
      />

      {publishedLinksData && (
        <PublishLinksDialog
          open={showPublishLinksDialog}
          onOpenChange={setShowPublishLinksDialog}
          links={publishedLinksData}
          type="email-health"
        />
      )}
    </div>
  );
}
